import type {
  Activity,
  AuthResult,
  Dashboard,
  Session,
  SessionsResult,
  User,
} from "./types";

const config = window.TIMETABLE_CONFIG ?? {
  apiBaseUrl: "https://api.timetable.rickykashyap.top/api/v1",
  googleClientId: "",
};

const TOKEN_KEY = "timetable_access_token";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function googleClientId(): string {
  return config.googleClientId.trim();
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  token: string | null = getStoredToken(),
): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (token !== null) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${config.apiBaseUrl}${path}`, { ...options, headers });
  if (!response.ok) {
    let message = "The request could not be completed.";
    let code = "REQUEST_FAILED";
    try {
      const body = (await response.json()) as {
        error?: { message?: string; code?: string };
      };
      message = body.error?.message ?? message;
      code = body.error?.code ?? code;
    } catch {
      // Keep the safe generic message for non-JSON failures.
    }
    if (response.status === 401 && token !== null) {
      clearToken();
    }
    throw new ApiError(message, response.status, code);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  const payload = (await response.json()) as { data: T };
  return payload.data;
}

export const api = {
  login(email: string, password: string) {
    return request<AuthResult>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }, null);
  },
  register(email: string, password: string, displayName: string, timezone: string) {
    return request<AuthResult>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, displayName, timezone }),
    }, null);
  },
  google(credential: string, timezone: string) {
    return request<AuthResult>("/auth/google", {
      method: "POST",
      body: JSON.stringify({ credential, timezone }),
    }, null);
  },
  me() {
    return request<{ user: User }>("/auth/me");
  },
  logout() {
    return request<void>("/auth/logout", { method: "POST" });
  },
  deleteAccount() {
    return request<void>("/auth/account", { method: "DELETE" });
  },
  activities(includeArchived = false) {
    return request<{ activities: Activity[] }>(
      `/activities${includeArchived ? "?includeArchived=true" : ""}`,
    );
  },
  createActivity(input: {
    name: string;
    category?: string | null;
    color?: string;
    icon?: string;
    targetMinutes?: number | null;
    targetPeriod?: "day" | "week" | "month" | null;
  }) {
    return request<{ activity: Activity }>("/activities", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateActivity(id: string, input: Partial<{
    name: string;
    category: string | null;
    color: string;
    icon: string;
    targetMinutes: number | null;
    targetPeriod: "day" | "week" | "month" | null;
  }>) {
    return request<{ activity: Activity }>(`/activities/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  archiveActivity(id: string) {
    return request<void>(`/activities/${id}`, { method: "DELETE" });
  },
  restoreActivity(id: string) {
    return request<{ activity: Activity }>(`/activities/${id}/restore`, { method: "POST" });
  },
  dashboard() {
    return request<Dashboard>("/analytics/dashboard");
  },
  sessions(pageSize = 20) {
    return request<SessionsResult>(`/sessions?pageSize=${pageSize}`);
  },
  startTimer(activityId: string) {
    return request<{ timer: Session }>("/timer/start", {
      method: "POST",
      body: JSON.stringify({ activityId }),
    });
  },
  pauseTimer(id: string) {
    return request<{ timer: Session }>(`/timer/${id}/pause`, { method: "POST" });
  },
  resumeTimer(id: string) {
    return request<{ timer: Session }>(`/timer/${id}/resume`, { method: "POST" });
  },
  stopTimer(id: string) {
    return request<{ session: Session }>(`/timer/${id}/stop`, { method: "POST" });
  },
  createSession(input: {
    activityId: string;
    startedAt: string;
    endedAt: string;
    note?: string | null;
  }) {
    return request<{ session: Session }>("/sessions", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateSession(id: string, input: {
    activityId?: string;
    startedAt?: string;
    endedAt?: string;
    note?: string | null;
  }) {
    return request<{ session: Session }>(`/sessions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  deleteSession(id: string) {
    return request<void>(`/sessions/${id}`, { method: "DELETE" });
  },
};
